import React, { useContext } from "react";
import PropTypes from "prop-types";
import { ConfigContext } from "../../Config/ConfigContext";
import { Box } from "@mui/material";
import MicrosoftOAuthProvider from ".";
import MicrosoftLogin from "./MicrosoftLogin";
import useApi from "../../hooks/useApi";

/**
 * MicrosoftOAuthChoice component.
 *
 * This component uses MicrosoftOAuthProvider to wrap MicrosoftLogin, initiating the Microsoft OAuth login process.
 * On a successful login, it constructs an authentication URL using the provided Microsoft OAuth details and performs a GET request.
 * Any API errors encountered during initialization are surfaced to the parent via the handleAuthError callback.
 *
 * @component
 * @param {object} props - Component properties.
 * @param {function} props.setAuthSuccess - Callback function to set authentication success.
 * @param {function} props.handleAuthError - Callback function to surface authentication errors to the parent UI.
 * @returns {JSX.Element} A React element.
 */
export default function MicrosoftOAuthChoice(props) {
	const { config } = useContext(ConfigContext);
	const api = useApi();

	return (
		<MicrosoftOAuthProvider clientId={config.microsoftOAuth.clientId}>
			<Box sx={{}}>
				<MicrosoftLogin
					ux_mode="redirect"
					onSuccess={async (msalResponse) => {
						try {
							await api.auth.init({
								params: {
									oauthResponse: JSON.stringify(msalResponse),
									signInPlatform: "microsoft",
									signInToken: msalResponse.idToken
								}
							});
							props.setAuthSuccess(true);
						} catch (err) {
							// Surface API error to parent so it can render an Alert
							if (typeof props.handleAuthError === "function") {
								props.handleAuthError(err);
							}
							// eslint-disable-next-line no-console
							console.error("Microsoft auth initialization failed:", err);
						}
					}}
				/>
			</Box>
		</MicrosoftOAuthProvider>
	);
}

MicrosoftOAuthChoice.propTypes = {
	setAuthSuccess: PropTypes.func.isRequired,
	handleAuthError: PropTypes.func
};